import { useLanguage } from "@/contexts/LanguageContext";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CIM_TEMPLATES, CIMTemplate, INDUSTRY_OPTIONS } from "@/constants/cim-templates.constants";
import { FileText, BarChart3 } from "lucide-react";

const translations = {
  en: {
    selectTemplate: "Select Industry Template",
    templateDescription: "Choose an industry-specific template to improve AI generation quality",
    currentTemplate: "Current Template",
    keyMetrics: "Key Metrics",
    valuationFactors: "Valuation Factors",
    noTemplate: "No template selected",
  },
  fr: {
    selectTemplate: "Sélectionner un modèle d'industrie",
    templateDescription: "Choisissez un modèle spécifique à l'industrie pour améliorer la qualité de la génération IA",
    currentTemplate: "Modèle actuel",
    keyMetrics: "Métriques clés",
    valuationFactors: "Facteurs de valorisation",
    noTemplate: "Aucun modèle sélectionné",
  },
};

interface CIMTemplateSelectorProps {
  selectedIndustry: string;
  onSelect: (industry: string, template: CIMTemplate) => void;
}

export function CIMTemplateSelector({ selectedIndustry, onSelect }: CIMTemplateSelectorProps) {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;

  const currentTemplate = CIM_TEMPLATES[selectedIndustry] || CIM_TEMPLATES.default;

  const handleSelect = (value: string) => {
    const template = CIM_TEMPLATES[value] || CIM_TEMPLATES.default;
    onSelect(value, template);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5" />
          {t.selectTemplate}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t.templateDescription}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>{t.currentTemplate}</Label>
          <Select value={selectedIndustry} onValueChange={handleSelect}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder={t.noTemplate} />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {language === "fr" ? option.labelFr : option.labelEn}
                </SelectItem>
              ))}
              <SelectItem value="default">
                {language === "fr" ? "Entreprise générale" : "General Business"}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {currentTemplate && (
          <div className="space-y-3 pt-2 border-t">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t.keyMetrics}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {currentTemplate.keyMetrics.map((metric) => (
                  <Badge key={metric} variant="secondary" className="text-xs">
                    {metric}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium">{t.valuationFactors}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {currentTemplate.valuationFactors.map((factor) => (
                  <Badge key={factor} variant="outline" className="text-xs">
                    {factor}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
